import { useMutation } from "@apollo/client";
import { GoalVisibility } from "types/graphql-schema";

import getArtifactSidebarQuery from "@apps/artifact-sidebar/graphql/get-artifact-sidebar-query";
import useLabel from "@apps/use-label/use-label";
import { buttonTheme } from "@components/button/button";
import GoalVisibilityIcon from "@components/goal-visibility-icon/goal-visibility-icon";
import DeprecatedSelect, { SelectOption } from "@components/select/select";

import updateGoalVisibilityMutation from "../graphql/update-goal-visibility-mutation";

export const GoalVisibilityDropdownInput = ({
  onChangeVisibility,
  artifact,
}: {
  artifact: {
    canUpdate: {
      permission: boolean;
    };
    goalVisibility: GoalVisibility;
  };
  onChangeVisibility: (option: SelectOption<GoalVisibility>) => void;
}) => {
  const label = useLabel();
  return (
    <DeprecatedSelect<GoalVisibility>
      disabled={!artifact.canUpdate.permission}
      options={[
        {
          value: GoalVisibility.Public,
          label: (
            <span className="flex items-center gap-1">
              <GoalVisibilityIcon
                goalVisibility={GoalVisibility.Public}
                size="4"
              />
              Public
            </span>
          ),
          description: `Visible to all ${label(
            "organization"
          )} members and the ${label("goal")} participants`,
        },
        {
          value: GoalVisibility.Private,
          label: (
            <span className="flex items-center gap-1">
              <GoalVisibilityIcon
                goalVisibility={GoalVisibility.Private}
                size="4"
              />
              Private
            </span>
          ),
          description: `Visible only to ${label("goal")} participants`,
        },
      ]}
      value={artifact.goalVisibility}
      onChange={onChangeVisibility}
      buttonProps={{
        theme: buttonTheme.text,
        "aria-label": "Goal visibility dropdown button",
      }}
    />
  );
};

const GoalVisibilityDropdown = ({
  artifact,
}: {
  artifact: {
    id: number;
    canUpdate: {
      permission: boolean;
    };
    goalVisibility: GoalVisibility;
  };
}) => {
  const [updateArtifact] = useMutation(updateGoalVisibilityMutation);

  const handleChangeVisibility = ({ value: newVisibility }: SelectOption) => {
    updateArtifact({
      variables: {
        artifactId: artifact.id,
        additionalFields: { goalVisibility: newVisibility },
      },
      refetchQueries: [getArtifactSidebarQuery],
    });
  };

  return (
    <GoalVisibilityDropdownInput
      artifact={artifact}
      onChangeVisibility={handleChangeVisibility}
    />
  );
};

export default GoalVisibilityDropdown;
